import React from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

interface GradientStackedAreaChartProps {
  data: { data: { timestamp: string; [key: string]: any }[] };
  title: string;
  yAxisName: string;
  height: string;
  target: number | null;
  seriesConfig: {
    name: string;
    color: string;
    dataField: string;
  }[];
  unitLabel?: string;
}

const GradientStackedAreaChart: React.FC<GradientStackedAreaChartProps> = ({
  data,
  title,
  height,
  seriesConfig,
  unitLabel = "คน"
}) => {
  const safeData = Array.isArray(data?.data) ? data.data : [];

  // Dynamically reorder series so that smaller values are rendered on top
  const reorderedSeriesConfig = React.useMemo(() => {
    if (!seriesConfig || seriesConfig.length <= 1) return seriesConfig;

    // Find the "All building users" series (should always be at the bottom)
    const allUsersIndex = seriesConfig.findIndex(
      config => config.dataField === "total_people_count"
    );

    // Separate the base series from the overlay series
    const baseSeries = allUsersIndex >= 0 ? [seriesConfig[allUsersIndex]] : [];
    const overlaySeries = seriesConfig.filter((_, index) => index !== allUsersIndex);

    // If we have overlay series, calculate their average values and sort
    if (overlaySeries.length > 1) {
      const seriesWithAverages = overlaySeries.map(config => {
        const total = safeData.reduce((sum, item) => {
          return sum + (item[config.dataField] || 0);
        }, 0);
        const average = safeData.length > 0 ? total / safeData.length : 0;
        return { config, average };
      });

      // Sort by average in descending order (higher values first, so they render at the bottom)
      seriesWithAverages.sort((a, b) => b.average - a.average);

      // Combine: base series first, then sorted overlay series
      return [...baseSeries, ...seriesWithAverages.map(item => item.config)];
    }

    // If no reordering needed, return original with base first
    return [...baseSeries, ...overlaySeries];
  }, [seriesConfig, safeData]);

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    slots.push('23:59');
    return slots;
  };

  const allTimeSlots = generateTimeSlots();

  // Round a value to a "nice" multiple of 10 based on its magnitude
  // Works dynamically for any scale (small or large numbers)
  const roundToNiceMultipleOf10 = (value: number) => {
    if (value <= 0) return 10;

    // Find the order of magnitude (e.g., 850 -> 100, 8500 -> 1000)
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));

    // Normalize value to range [1, 10)
    const normalizedValue = value / magnitude;

    // Choose nice multiplier: 1, 2, 5, or 10
    // This gives us nice round numbers like 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, etc.
    let niceMultiplier;
    if (normalizedValue <= 1) {
      niceMultiplier = 1;
    } else if (normalizedValue <= 2) {
      niceMultiplier = 2;
    } else if (normalizedValue <= 5) {
      niceMultiplier = 5;
    } else {
      niceMultiplier = 10;
    }

    const niceValue = niceMultiplier * magnitude;

    // Ensure it's at least 10 and a multiple of 10
    return Math.max(10, Math.ceil(niceValue / 10) * 10);
  };

  // Calculate Y-axis values to show exactly 5 rows (0, val1, val2, val3, max)
  // All values must be multiples of 10 with no decimals
  // Truly dynamic - works for any scale automatically
  const calculateYAxisValues = () => {
    const maxValue = Math.max(...safeData.map(item => item.total_people_count || 0));

    if (maxValue <= 0) {
      return { max: 40, interval: 10 };
    }

    // Add 10% padding to max value
    const paddedMax = maxValue * 1.1;

    // Calculate raw interval for 4 intervals (which creates 5 values: 0, 1, 2, 3, 4)
    const rawInterval = paddedMax / 4;

    // Round to nice multiple of 10 dynamically
    const interval = roundToNiceMultipleOf10(rawInterval);

    // Recalculate max to ensure it's exactly interval * 4
    const finalMax = interval * 4;

    return { max: finalMax, interval: interval };
  };

  const { max: extendedMaxYValue, interval: yAxisInterval } = calculateYAxisValues();


  const option = {
    title: title,
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        label: {
          backgroundColor: "#6a7985",
        },
      },
      formatter: function (params: any) {
        let result = `${params[0].axisValue}<br/>`;

        // Reorder params to match the original seriesConfig order
        const orderedParams = seriesConfig?.map(config => {
          return params.find((p: any) => p.seriesName === config.name);
        }).filter(Boolean) || params;

        orderedParams.forEach((param: any) => {
          const config = seriesConfig?.find(c => c.name === param.seriesName);
          const color = config?.color || param.color;
          result += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;
          result += `${param.seriesName}: ${param.value}<br/>`;
        });
        return result;
      },
    },
    legend: {
      data: seriesConfig?.map(config => ({
        name: config.name,
        itemStyle: {
          color: config.color
        }
      })) || [],
      icon: "roundRect",
      itemWidth: 14,
      itemHeight: 14,
      textStyle: {
        fontSize: 13,
        color: "#000",
        marginTop: 10,
      },
      bottom: "1%",
    },
    grid: {
      show: true,
      borderColor: "#e0e0e0",
    },

    xAxis: [
      {
        type: "category",
        boundaryGap: false,
        data: allTimeSlots,
        axisLabel: {
          interval: function (_index: number, value: string) {
            const targetTimes = ["00:00", "04:00", "08:00", "12:00", "16:00", "20:00", "23:59"];
            return targetTimes.includes(value);
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#e0e0e0",
            type: "solid",
          },
        },
      },
    ],

    yAxis: [
      {
        interval: yAxisInterval,
        min: 0,
        max: extendedMaxYValue,
        type: "value",
        axisLabel: {
          formatter: function (value: number) {
            if (value === extendedMaxYValue) return unitLabel;
            return Math.round(value).toString();
          },
          color: "#333",
          fontSize: 14,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#e0e0e0",
            type: "solid",
          },
        },
      },
    ],
    series: reorderedSeriesConfig?.map(config => {
      const mappedData = allTimeSlots.map(timeSlot => {
        if (timeSlot === '23:59') {
          const matchingItem = safeData.find(item => {
            const date = new Date(item.timestamp);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            if (hours === 0 && minutes === 0) {
              const firstDate = new Date(safeData[0]?.timestamp);
              return date.getTime() > firstDate.getTime();
            }
            return false;
          });
          return matchingItem ? (matchingItem[config.dataField] || 0) : 0;
        }
        
        const matchingItem = safeData.find(item => {
          const date = new Date(item.timestamp);
          const hours = date.getHours().toString().padStart(2, "0");
          const minutes = date.getMinutes().toString().padStart(2, "0");
          return `${hours}:${minutes}` === timeSlot;
        });
        return matchingItem ? (matchingItem[config.dataField] || 0) : 0;
      });
      
      return {
        name: config.name,
        type: "line",
        smooth: true,
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 1,
          color: config.color,
        },
        emphasis: {
          focus: "series",
        },
        data: mappedData,
      };
    }) || [],
  };
  return (
    <ReactECharts
      option={option}
      echarts={echarts}
      style={{ height: height, width: "100%", marginTop: -60 }}
    />
  );
};

export default GradientStackedAreaChart;
